import { useEffect, useState, useMemo } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Pencil, Trash2, Search, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import CompanyFormDialog from "../components/companies/CompanyFormDialog";

const PAGE_SIZE = 30;

export default function Companies() {
    const { hasPermission } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState({ key: null, dir: 0 }); // 0=default, 1=asc, 2=desc
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [selected, setSelected] = useState(new Set());

    const canManage = hasPermission("companies_manage");
    const canBulkDelete = canManage;

    const load = async () => { const { data } = await api.get("/companies"); setItems(data); setSelected(new Set()); };
    useEffect(() => { load(); }, []);

    const cycleSort = (key) => {
        setSort((s) => {
            if (s.key !== key) return { key, dir: 1 };
            if (s.dir === 1) return { key, dir: 2 };
            return { key: null, dir: 0 };
        });
        setPage(1);
    };

    const sortIcon = (key) => {
        if (sort.key !== key) return null;
        return sort.dir === 1 ? <ArrowUp className="w-3 h-3 inline ml-1"/> : <ArrowDown className="w-3 h-3 inline ml-1"/>;
    };

    const filtered = useMemo(() => {
        const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const term = norm(q.trim());
        const list = !term ? items : items.filter(c => {
            // Prefix match (starts with) across all relevant fields
            return norm(c.name).startsWith(term)
                || norm(c.email).startsWith(term)
                || norm(c.phone).startsWith(term)
                || norm(c.city).startsWith(term);
        });
        if (!sort.key || sort.dir === 0) return list;
        const sorted = [...list].sort((a, b) => {
            const av = (a[sort.key]||"").toString().toLowerCase();
            const bv = (b[sort.key]||"").toString().toLowerCase();
            return sort.dir === 1 ? av.localeCompare(bv) : bv.localeCompare(av);
        });
        return sorted;
    }, [items, q, sort]);

    useEffect(() => { setPage(1); }, [q]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageItems = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

    const openNew = () => { setEditing(null); setOpen(true); };
    const openEdit = (c) => { setEditing(c); setOpen(true); };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar empresa?")) return;
        await api.delete(`/companies/${id}`); toast.success("Eliminada"); load();
    };

    const removeSelected = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        if (!window.confirm(`¿Eliminar ${ids.length} empresa(s) seleccionada(s)? Esta acción es irreversible.`)) return;
        try {
            const { data } = await api.post("/companies/bulk-delete", { company_ids: ids });
            toast.success(`Eliminadas: ${data.deleted}`);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const allOnPageSelected = pageItems.length > 0 && pageItems.every(c => selected.has(c.company_id));

    const togglePageAll = () => {
        const next = new Set(selected);
        if (allOnPageSelected) pageItems.forEach(c => next.delete(c.company_id));
        else pageItems.forEach(c => next.add(c.company_id));
        setSelected(next);
    };

    const toggleOne = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };

    const Th = ({ k, label }) => (
        <th
            className="text-left px-4 py-3 cursor-pointer select-none hover:text-orange-600 transition-colors"
            onClick={() => cycleSort(k)}
            data-testid={`companies-sort-${k}`}
        >
            {label}{sortIcon(k)}
        </th>
    );

    return (
        <Layout
            title="Empresas"
            subtitle="Cuentas con las que trabajas"
            actions={
                <>
                    {canBulkDelete && selected.size > 0 && (
                        <Button variant="outline" onClick={removeSelected} className="text-red-600 hover:text-red-700 border-red-300" data-testid="companies-bulk-delete-button">
                            <Trash2 className="w-4 h-4 mr-2"/>Eliminar seleccionadas ({selected.size})
                        </Button>
                    )}
                    {canManage && <Button onClick={openNew} data-testid="company-new-button"><Plus className="w-4 h-4 mr-2"/>Nueva Empresa</Button>}
                </>
            }
        >
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-400"/>
                    <Input
                        value={q}
                        onChange={(e)=>setQ(e.target.value)}
                        placeholder="Buscar por nombre, email, teléfono o ciudad..."
                        className="border-0 focus-visible:ring-0 shadow-none"
                        data-testid="companies-search-input"
                    />
                </div>
                <div className="overflow-x-auto scrollbar-thin">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                                {canBulkDelete && (
                                    <th className="px-3 py-3 w-10">
                                        <Checkbox
                                            checked={allOnPageSelected}
                                            onCheckedChange={togglePageAll}
                                            data-testid="companies-select-all"
                                            aria-label="Seleccionar todas"
                                        />
                                    </th>
                                )}
                                <Th k="name" label="Nombre"/>
                                <Th k="industry" label="Industria"/>
                                <Th k="email" label="Email"/>
                                <Th k="phone" label="Teléfono"/>
                                <Th k="city" label="Ciudad"/>
                                <th className="text-right px-4 py-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((c) => (
                                <tr key={c.company_id} className={`border-t border-slate-100 hover:bg-slate-50 ${selected.has(c.company_id) ? "bg-orange-50/40" : ""}`} data-testid={`company-row-${c.company_id}`}>
                                    {canBulkDelete && (
                                        <td className="px-3 py-3">
                                            <Checkbox
                                                checked={selected.has(c.company_id)}
                                                onCheckedChange={()=>toggleOne(c.company_id)}
                                                data-testid={`company-select-${c.company_id}`}
                                                aria-label="Seleccionar empresa"
                                            />
                                        </td>
                                    )}
                                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                                    <td className="px-4 py-3 text-slate-600">{c.industry || "—"}</td>
                                    <td className="px-4 py-3 text-slate-600">{c.email || "—"}</td>
                                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{c.phone || "—"}</td>
                                    <td className="px-4 py-3 text-slate-600">{c.city || "—"}{c.country ? `, ${c.country}` : ""}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        {canManage && <>
                                            <button onClick={()=>openEdit(c)} title="Editar" className="inline-flex p-1.5 rounded hover:bg-slate-100 mx-0.5" data-testid={`company-edit-${c.company_id}`}><Pencil className="w-3.5 h-3.5 text-slate-600"/></button>
                                            <button onClick={()=>remove(c.company_id)} title="Eliminar" className="inline-flex p-1.5 rounded hover:bg-red-50 mx-0.5" data-testid={`company-delete-${c.company_id}`}><Trash2 className="w-3.5 h-3.5 text-red-500"/></button>
                                        </>}
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && <tr><td colSpan={canBulkDelete ? 7 : 6} className="px-4 py-12 text-center text-slate-400">Sin empresas</td></tr>}
                        </tbody>
                    </table>
                </div>
                {filtered.length > 0 && (
                    <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-sm" data-testid="companies-pagination">
                        <div className="text-slate-500">
                            Mostrando {(page-1)*PAGE_SIZE + 1}–{Math.min(page*PAGE_SIZE, filtered.length)} de {filtered.length}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} data-testid="companies-page-prev">Anterior</Button>
                            <span className="text-slate-600 font-mono text-xs px-2">Página {page} / {totalPages}</span>
                            <Button variant="outline" size="sm" onClick={()=>setPage(p=>Math.min(totalPages, p+1))} disabled={page>=totalPages} data-testid="companies-page-next">Siguiente</Button>
                        </div>
                    </div>
                )}
            </div>

            <CompanyFormDialog open={open} onOpenChange={setOpen} company={editing} onSaved={load} />
        </Layout>
    );
}
